import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import ItemTypeAndBookedLabel from '../Common/ItemTypeAndBookedLabel'
import StartAndEndLocation from '../Common/StartAndEndLocation'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalImageThumbnail from 'LuxKit/Modal/ModalImageThumbnail'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import offerPageURL from 'lib/offer/offerPageURL'
import { isLEOffer } from 'lib/offer/offerTypes'
import BookingsDetails from 'tripPlanner/components/TripModal/TripItems/Summary/Common/BookingsDetails'
import CheckInAndOutDate from 'tripPlanner/components/TripModal/TripItems/Summary/Common/CheckInAndOutDate'
import OrderTravellers from 'tripPlanner/components/TripModal/TripItems/Summary/Common/OrderTravellers'
import { useOrder } from 'tripPlanner/hooks/api/order'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import type { CruiseItem, TourItem } from 'tripPlanner/types/tripItem'
import { getOrderId } from 'tripPlanner/utils'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import { useIntl } from 'react-intl'

interface Props {
  offer: App.Offer
  item: CruiseItem | TourItem
}

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding: 0 0 ${rem(12)} 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
`

function TourAndCruiseSummaryContent({ item, offer }: Props) {
  const intl = useIntl()
  const showDates =
    item.endDate || item.startDate || item.startDay || item.endDay
  const orderId = getOrderId(item)

  const { data: order, isLoading } = useOrder(orderId)
  const offerUrl = offerPageURL(offer)

  const { setImmersiveMode } = useImmersiveModeHandlers()

  const setTripId = useCallback(() => {
    setImmersiveMode({ tripId: item.tripId, sectionId: tripItemSelectionId(item.id) })
  }, [setImmersiveMode, item])

  return (
    <>
      {isLoading && <LoadingIndicator inline />}
      <ModalImageThumbnail image={offer.image} />
      <ModalContent>
        {!isLoading && (
          <VerticalSpacer gap={20}>
            <ItemTypeAndBookedLabel item={item} />
            <Header>
              <div>
                <ProductTypeLabel productType={offer.productType} />
              </div>
              <BodyText variant="large">{offer.description}</BodyText>
              {offerUrl && (
                <TextLink weight="bold" to={offerUrl} onClick={setTripId}>
                  {intl.formatMessage({
                    id: '8S6iEWol+8',
                    defaultMessage: 'View offer details',
                    description: 'Link to view tour or cruise offer details in trip planner',
                  })}
                </TextLink>
              )}
            </Header>
            {order && (
              <BookingsDetails
                order={order}
                showOrderId={false}
                orderItemType="offer"
              />
            )}
            {order && isLEOffer(offer) && (
              <OrderTravellers orderId={order.id} />
            )}
            {showDates && (
              <CheckInAndOutDate
                isAccommodation={false}
                startDate={item.startDate}
                endDate={item.endDate}
                startTime={item.startTime}
                endTime={item.endTime}
              />
            )}
            <StartAndEndLocation
              startPlace={item.startPlace}
              endPlace={item.endPlace}
            />
          </VerticalSpacer>
        )}
      </ModalContent>
    </>
  )
}

export default TourAndCruiseSummaryContent
